/* eslint-disable camelcase */
import React, { Component, createRef } from "react";
import { Link } from "react-router";
import classNames from "classnames";
import MaskedInput from "react-text-mask";
import { StateSelection } from "@components/common";
import { SignUpService, StripeService, OfficerService } from "@services";
import { BANK_ACCOUNT_TYPES, STATIC_URLS, MASK_VALUE } from "@constants";
import utils from "@utils";
import { PanelControls } from "./components";
import { Image, AsyncSelect, ReactSelect, Loader } from "../../../components";

import TextLogo from "../../../images/logo-text.svg";
import IconLogo from "../../../images/logo-icon.svg";
import "./CoordinatorSignup.scss";

class CoordinatorSignup extends Component {
	constructor(props) {
		super(props);

		const { lang } = global;

		this._stateSelection = createRef();

		this.stepArray = [
			lang.t("Sign Up"),
			lang.t("Coordinator/Dept Details"),
			lang.t("Bank Account Info"),
		];
		this.bankAccountTypeOptions = Object.values(BANK_ACCOUNT_TYPES);

		this.initialValidations = {
			personalInfo: {
				FirstName: "",
				LastName: "",
				Email: "",
				MobilePhone: "",
				CompanyName: "",
				Password: "",
				_serviceTerms: "",
			},
			contactInfo: {
				StreetAddress: "",
				City: "",
				StateId: "",
				Zip: "",
			},
			bankAccountInfo: {
				account_holder_name: false, // true = has error
				routing_number: false,
				account_number: false,
				account_number_confirm: false,
				account_holder_type: false,
				id_number: false,
				dateOfBirth: false,
				tax_id: false,
				name: false,
				line1: false,
				city: false,
				state: false,
				postal_code: false,
				photoId: false,
				fullyAuthorized: false,
			},
			coordinatorDetails: {
				JurisdictionId: "",
				OfficerRankId: "",
				YearsOfService: "",
				YearsWithCurrentDepartment: "",
				PermittedToCharge: "",
				NumberOfLocations: "",
				ShiftsPerWeek: "",
			},
		};

		this.state = {
			step: 0,
			loading: false,
			signUpToken: "",
			officerRanks: [],
			validations: { ...this.initialValidations },
			form: {
				personalInfo: {
					FirstName: "",
					LastName: "",
					Email: "",
					MobilePhone: "",
					CompanyName: "",
					Password: "",
					ConfirmPassword: "",
					_serviceTerms: "",
				},
				contactInfo: {
					StreetAddress: "",
					City: "",
					StateId: "",
					Zip: "",
				},
				bankAccountInfo: {
					account_holder_name: "",
					routing_number: "",
					account_number: "",
					account_number_confirm: "",
					account_holder_type: "1",
					id_number: "",
					dateOfBirth: "",
					tax_id: "",
					name: "",
					line1: "",
					city: "",
					state: "",
					postal_code: "",
					photoId: null,
					photoPreview: "",
					fullyAuthorized: false,
					validImageSelected: true,
				},
				coordinatorDetails: {
					_isLeaOfficer: "no",
					JurisdictionId: "",
					OfficerRankId: "",
					YearsOfService: "",
					YearsWithCurrentDepartment: "",
					PermittedToCharge: "no",
					NumberOfLocations: "",
					ShiftsPerWeek: "",
				},
			},
		};
	}

	incrementStep = () => {
		const { step } = this.state;
		this.setState({ step: step + 1 });
	};

	decrementStep = () => {
		const { step } = this.state;
		this.setState({ step: step - 1 });
	};

	/** Validates the personal information section of the coordinator sign up */
	validatePersonalInfoForm = () => {
		const { lang } = global;
		this.setState(prevState => ({
			validations: {
				...prevState.validations,
				personalInfo: this.initialValidations.personalInfo,
			},
		}));
		const {
			form: { personalInfo },
		} = this.state;
		const newValidations = {};

		newValidations.FirstName = personalInfo.FirstName === "";
		newValidations.LastName = personalInfo.LastName === "";
		newValidations.Email = personalInfo.Email === "";
		newValidations.MobilePhone = personalInfo.MobilePhone.replace(/\D+/g, "").length !== 10;
		newValidations.CompanyName = personalInfo.CompanyName === "";
		newValidations.Password =
			personalInfo.Password === "" || personalInfo.Password !== personalInfo.ConfirmPassword;
		newValidations._serviceTerms = !personalInfo._serviceTerms;

		if (!personalInfo._serviceTerms) {
			utils.notify({
				type: "error",
				message: lang.t("You must agree to terms of service"),
			});
		}

		if (personalInfo.Password !== personalInfo.ConfirmPassword) {
			utils.notify({
				type: "error",
				message: lang.t("Passwords do not match"),
			});
		}

		if (newValidations.MobilePhone) {
			utils.notify({
				type: "error",
				message: lang.t("The phone number must be 10 digits long_"),
			});
		}

		this.setState(prevState => ({
			validations: {
				...prevState.validations,
				personalInfo: newValidations,
			},
		}));

		if (!Object.values(newValidations).includes(true)) {
			this.setState({ step: 1 });
		}
	};

	/** Validates the contact information section of the coordinator sign up */
	validateContactInfoForm = () => {
		const {
			form: { contactInfo },
		} = this.state;
		const newValidations = {};

		this.setState(prevState => ({
			validations: {
				...prevState.validations,
				contactInfo: this.initialValidations.contactInfo,
			},
		}));

		newValidations.StreetAddress = contactInfo.StreetAddress === "";
		newValidations.City = contactInfo.City === "";
		newValidations.StateId = contactInfo.StateId === "";
		newValidations.Zip = contactInfo.Zip === "";

		this.setState(prevState => ({
			validations: {
				...prevState.validations,
				contactInfo: newValidations,
			},
		}));

		if (!Object.values(newValidations).includes(true)) {
			this.createCoordinator();
		}
	};

	/** Validates the coordinator details section of the coordinator sign up */
	validateCoordinatorDetailsForm = () => {
		const {
			form: { coordinatorDetails },
		} = this.state;
		const newValidations = {};

		this.setState(prevState => ({
			validations: {
				...prevState.validations,
				coordinatorDetails: this.initialValidations.coordinatorDetails,
			},
		}));

		if (coordinatorDetails._isLeaOfficer === "yes") {
			newValidations.JurisdictionId = coordinatorDetails.JurisdictionId === "";
			newValidations.OfficerRankId = coordinatorDetails.OfficerRankId === "";
			newValidations.YearsOfService = coordinatorDetails.YearsOfService === "";
			newValidations.YearsWithCurrentDepartment =
				coordinatorDetails.YearsWithCurrentDepartment === "";
		}
		newValidations.PermittedToCharge = coordinatorDetails.PermittedToCharge === "";
		newValidations.NumberOfLocations = coordinatorDetails.NumberOfLocations === "";
		newValidations.ShiftsPerWeek = coordinatorDetails.ShiftsPerWeek === "";

		this.setState(prevState => ({
			validations: {
				...prevState.validations,
				coordinatorDetails: newValidations,
			},
		}));

		if (!Object.values(newValidations).includes(true)) {
			this.saveCoordinatorDetails();
		}
	};

	/** Validates the bank account information section of the coordinator sign up */
	validateBankAccountForms = () => {
		const { lang } = global;
		const {
			form: { bankAccountInfo, personalInfo },
			validations,
		} = this.state;
		const bankAccountType = parseInt(bankAccountInfo.account_holder_type, 10);

		const isMobilePhoneValid = personalInfo.MobilePhone.replace(/\D+/g, "").length === 10;
		const isNameValid = bankAccountInfo.account_holder_name.split(" ").length > 1;
		const validationErrors = { ...validations.bankAccountInfo };

		validationErrors.account_holder_name =
			bankAccountInfo.account_holder_name === "" || !isNameValid;
		validationErrors.routing_number = bankAccountInfo.routing_number === "";
		validationErrors.account_number = bankAccountInfo.account_number === "";
		validationErrors.account_number_confirm =
			bankAccountInfo.account_number_confirm === "" ||
			bankAccountInfo.account_number !== bankAccountInfo.account_number_confirm;
		validationErrors.line1 = bankAccountInfo.line1 === "";
		validationErrors.city = bankAccountInfo.city === "";
		validationErrors.state = bankAccountInfo.state === "";
		validationErrors.postal_code = bankAccountInfo.postal_code === "";
		validationErrors.fullyAuthorized =
			bankAccountInfo.fullyAuthorized === "" || bankAccountInfo.fullyAuthorized === false;
		validationErrors.id_number =
			bankAccountType === BANK_ACCOUNT_TYPES.INDIVIDUAL.value &&
			bankAccountInfo.id_number === "";
		validationErrors.dateOfBirth =
			bankAccountType === BANK_ACCOUNT_TYPES.INDIVIDUAL.value &&
			bankAccountInfo.dateOfBirth === "";
		validationErrors.photoId =
			bankAccountType === BANK_ACCOUNT_TYPES.INDIVIDUAL.value &&
			bankAccountInfo.photoId === null;
		validationErrors.tax_id =
			bankAccountType === BANK_ACCOUNT_TYPES.COMPANY.value && bankAccountInfo.tax_id === "";
		validationErrors.name =
			bankAccountType === BANK_ACCOUNT_TYPES.COMPANY.value && bankAccountInfo.name === "";

		// If any validation property is true, the form is not valid
		const validationError = Object.values(validationErrors).some(item => item);

		const isValid = !validationError && isMobilePhoneValid && isNameValid;

		this.setState(prevState => ({
			validations: {
				...prevState.validations,
				bankAccountInfo: validationErrors,
				personalInfo: {
					...prevState.validations.personalInfo,
					MobilePhone: !isMobilePhoneValid,
				},
			},
			loading: isValid,
		}));

		if (!isMobilePhoneValid) {
			utils.notify({
				type: "error",
				message: lang.t("The phone number must be 10 digits long_"),
			});
		}

		if (!isNameValid) {
			utils.notify({
				type: "error",
				message: lang.t("Please enter your first and last name"),
			});
		}

		if (!isValid) return;

		this.saveDirectDepositAccount();
	};

	/**
	 * Retrieves a list of officer ranks associated with a jurisdictoin
	 * @param {Number} jurisdictionId The unique id of the selected jursdiction
	 */
	getOfficerRanks = jurisdictionId => {
		const { lang } = global;

		OfficerService.getRanks({ jurisdictionId })
			.then(({ data }) => {
				this.setState({
					officerRanks: data.map(({ Value, Text }) => ({
						value: parseInt(Value, 10),
						label: Text,
					})),
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading officer ranks"),
				});
			});
	};

	/** Gathers all required information associated with creating a coordinators account  */
	createCoordinator = () => {
		const { lang } = global;
		const {
			form: { personalInfo },
		} = this.state;
		const formToSend = Object.fromEntries(
			Object.entries(personalInfo).filter(([key]) => !key.startsWith("_")),
		);

		// remove phone mask
		formToSend.MobilePhone = utils.formatPhoneNumber(formToSend.MobilePhone);

		this.setState({ loading: true });

		SignUpService.signUpCoordinator(null, formToSend)
			.then(({ data: { SignUpToken } }) => {
				utils.notify({
					type: "success",
					message: lang.t("Coordinator created successfully"),
				});

				this.setState({
					signUpToken: SignUpToken,
					loading: false,
					step: 2,
				});
			})
			.catch(
				({
					response: {
						data: { Message },
					},
				}) => {
					this.setState({ loading: false });

					utils.notify({
						type: "error",
						message: Message || lang.t("Error signing up user"),
					});
				},
			);
	};

	/** Saves all changes made to the coordinator details section */
	saveCoordinatorDetails = () => {
		const { lang } = global;
		const {
			signUpToken,
			form: { coordinatorDetails: rawCoordinatorDetails, contactInfo, personalInfo },
		} = this.state;
		// remove underscore properties
		const coordinatorDetails = Object.fromEntries(
			Object.entries(rawCoordinatorDetails).filter(([key]) => !key.startsWith("_")),
		);
		// parse radio value to boolean
		coordinatorDetails.PermittedToCharge = coordinatorDetails.PermittedToCharge === "yes";

		this.setState({ loading: true });

		SignUpService.saveCoordinatorDetails(
			{ token: signUpToken },
			{ ...coordinatorDetails, ...contactInfo },
		)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Coordinator details saved successfully"),
				});

				this.setState(prevState => ({
					loading: false,
					step: 3,
					form: {
						...prevState.form,
						// Set bank account billing info based on data provided at step 0
						bankAccountInfo: {
							...prevState.form.bankAccountInfo,
							account_holder_name: `${personalInfo.FirstName} ${personalInfo.LastName}`,
							line1: contactInfo.StreetAddress,
							city: contactInfo.City,
							state: contactInfo.StateId,
							postal_code: contactInfo.Zip,
						},
					},
				}));
			})
			.catch(() => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error saving coordinator details"),
				});
			});
	};

	updateForm =
		(section, integer = false) =>
		({ target: { name, value } }) => {
			this.setState(prevState => ({
				form: {
					...prevState.form,
					[section]: {
						...prevState.form[section],
						[name]: integer ? value.replace(/\D/g, "") : value,
					},
				},
			}));
		};

	updateBankAccountForms = section => (property, type) => event => {
		// Created its own update func since the form structure for those components is different.
		const updateForm = value => {
			this.setState(prevState => ({
				form: {
					...prevState.form,
					[section]: {
						...prevState.form[section],
						[property]: {
							...prevState.form[section][property],
							value,
						},
					},
				},
			}));
		};

		if (type === "select") {
			updateForm(event.value);
			return;
		}

		updateForm(event.target.value);
	};

	onPhotoChange = e => {
		const { files } = e.target;

		if (files && files[0]) {
			const {
				form: {
					bankAccountInfo: { photoPreview },
				},
			} = this.state;
			const [{ type }] = files;

			if (photoPreview) URL.revokeObjectURL(photoPreview);
			const newPhotoPreview = URL.createObjectURL(files[0]);
			const photoId = files[0];
			this.setState(prevState => ({
				form: {
					...prevState.form,
					bankAccountInfo: {
						...prevState.form.bankAccountInfo,
						validImageSelected: type.includes("image"),
						photoId: type.includes("image") ? photoId : null,
						photoPreview: type.includes("image") ? newPhotoPreview : "",
					},
				},
			}));
		}
	};

	updateSelect =
		section =>
		(event, { name }) => {
			const value = event?.value || "";
			this.setState(
				prevState => ({
					form: {
						...prevState.form,
						[section]: {
							...prevState.form[section],
							[name]: value,
						},
					},
				}),
				() => {
					if (name === "JurisdictionId") {
						this.getOfficerRanks(value);
					}
				},
			);
		};

	updatePassword = ({ target: { value } }) => {
		this.setState(prevState => ({
			form: {
				...prevState.form,
				personalInfo: {
					...prevState.form.personalInfo,
					Password: value,
				},
			},
		}));
	};

	updateServiceTerms = ({ target: { checked } }) => {
		this.setState(prevState => ({
			form: {
				...prevState.form,
				personalInfo: {
					...prevState.form.personalInfo,
					_serviceTerms: checked,
				},
			},
		}));
	};

	updateAccountAuthorization = ({ target: { checked } }) => {
		this.setState(prevState => ({
			form: {
				...prevState.form,
				bankAccountInfo: {
					...prevState.form.bankAccountInfo,
					fullyAuthorized: checked,
				},
			},
		}));
	};

	/** Saves all changes associated with direct deposit information set up by the coordinator which is signing up */
	saveDirectDepositAccount = async () => {
		const { lang } = global;
		let imageToken = { id: null };
		const {
			signUpToken,
			form: { personalInfo, bankAccountInfo },
		} = this.state;

		if (
			bankAccountInfo.account_holder_type === BANK_ACCOUNT_TYPES.INDIVIDUAL.value.toString()
		) {
			imageToken = await StripeService.createImageToken(bankAccountInfo.photoId);
			if (imageToken.error) {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: imageToken.error.message,
				});
				return;
			}
		}

		const { USStates } = this._stateSelection.current.state;
		const stateName = USStates.find(
			item => item.value === parseInt(bankAccountInfo.state, 10),
		).label.substr(5);

		const accountToken = await StripeService.createAccountToken(
			{
				...bankAccountInfo,
				first_name: personalInfo.FirstName,
				last_name: personalInfo.LastName,
				phoneNumber: personalInfo.MobilePhone,
				state: stateName,
			},
			imageToken.id,
		);
		if (accountToken.error) {
			this.setState({ loading: false });
			utils.notify({
				type: "error",
				message: accountToken.error.message,
			});
			return;
		}

		const bankAccountToken = await StripeService.createBankAccountToken(bankAccountInfo);
		if (bankAccountToken.error) {
			this.setState({ loading: false });
			utils.notify({
				type: "error",
				message: bankAccountToken.error.message,
			});
			return;
		}

		const data = {
			AccountTokenId: accountToken.token.id,
			BankAccount: {
				TokenId: bankAccountToken.token.id,
			},
		};

		SignUpService.saveDirectDepositAccount({ token: signUpToken }, data)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Direct Deposit Account created successfully"),
				});

				this.setState({
					loading: false,
					step: 4,
				});
			})
			.catch(({ response: { data: errorData } }) => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: errorData?.Message ?? lang.t("Error saving bank account details"),
				});
			});
	};

	componentDidMount = () => {
		this.getOfficerRanks();
	};

	render() {
		const { lang } = global;
		const { step, form, loading, validations, officerRanks } = this.state;

		return (
			<div id="coordinator-signup">
				<nav className="navbar navbar-default navbar-static-top">
					<div className="container-fluid">
						<div className="row">
							<div className="col-xs-4 navbar-header">
								<div className="navbar-brand">
									<img
										src={IconLogo}
										className="visible-xs brand-img"
										alt="Brand"
									/>
									<img
										src={TextLogo}
										className="hidden-xs brand-img"
										alt="Brand"
									/>
									<span className="visible-xs persona">
										{lang.t("Coordinator").substr(0, 5)}
									</span>
									<span className="hidden-xs persona">
										{lang.t("Coordinator")}
									</span>
								</div>
							</div>
							<div className="col-xs-8 sign-in">
								<span>{lang.t("Already a member_")}</span>
								<Link
									className="btn btn-default"
									to="/login"
								>
									{lang.t("Sign In")}
								</Link>
							</div>
						</div>
					</div>
				</nav>
				<div className="progress">
					<div
						role="progressbar"
						className={`progress-bar step${step}`}
						aria-valuenow={((step / 4) * 100).toFixed(2)}
						aria-valuemin="0"
						aria-valuemax="100"
					>
						<span className="sr-only">{`${((step / 4) * 100).toFixed(2)}% Complete`}</span>
					</div>
				</div>
				<div className="container-fluid">
					<div className="row">
						<div
							id="signup-panel"
							className="panel col-md-6 col-md-offset-1 col-lg-5 col-lg-offset-2"
						>
							<div
								id="signup-panel-header"
								className="row"
							>
								<h2 id="signup-title">
									<span>{lang.t("Coordinator")} </span>
									<span>{lang.t("Sign Up")}</span>
								</h2>
								<h4 id="signup-subtitle">
									{lang.t("We will get you setup in a few steps")}
								</h4>
							</div>
							<div
								id="signup-panel-body"
								className="row"
							>
								<Choose>
									<When condition={step === 0}>
										<div id="personal-info-form">
											<div className="info-message">
												{lang.t("Enter your personal information")}
											</div>
											<div className="row">
												<div
													className={classNames(
														"col-sm-6",
														"form-group",
														{
															"has-error":
																validations.personalInfo.FirstName,
														},
													)}
												>
													<label
														htmlFor="FirstName"
														className="control-label"
													>
														{lang.t("First Name")}
													</label>
													<input
														id="FirstName"
														name="FirstName"
														type="text"
														className="form-control"
														value={form.personalInfo.FirstName}
														onChange={this.updateForm("personalInfo")}
													/>
												</div>
												<div
													className={classNames(
														"col-sm-6",
														"form-group",
														{
															"has-error":
																validations.personalInfo.LastName,
														},
													)}
												>
													<label
														htmlFor="LastName"
														className="control-label"
													>
														{lang.t("Last Name")}
													</label>
													<input
														id="LastName"
														name="LastName"
														type="text"
														className="form-control"
														value={form.personalInfo.LastName}
														onChange={this.updateForm("personalInfo")}
													/>
												</div>
											</div>
											<div className="row">
												<div
													className={classNames(
														"col-sm-6",
														"form-group",
														{
															"has-error":
																validations.personalInfo.Email,
														},
													)}
												>
													<label
														htmlFor="Email"
														className="control-label"
													>
														{lang.t("EMail")}
													</label>
													<input
														id="Email"
														name="Email"
														type="email"
														className="form-control"
														value={form.personalInfo.Email}
														onChange={this.updateForm("personalInfo")}
														data-test-id="login-email-input"
													/>
												</div>
												<div
													className={classNames(
														"col-sm-6",
														"form-group",
														{
															"has-error":
																validations.personalInfo
																	.MobilePhone,
														},
													)}
												>
													<label
														htmlFor="MobilePhone"
														className="control-label"
													>
														{lang.t("Phone")}
													</label>
													<MaskedInput
														id="MobilePhone"
														name="MobilePhone"
														type="tel"
														mask={MASK_VALUE.phoneMask}
														className="form-control"
														value={form.personalInfo.MobilePhone}
														onChange={this.updateForm("personalInfo")}
													/>
												</div>
											</div>
											<div
												className={classNames("form-group", {
													"has-error":
														validations.personalInfo.CompanyName,
												})}
											>
												<label
													htmlFor="CompanyName"
													className="control-label"
												>
													{lang.t("Company")}
												</label>
												<input
													id="CompanyName"
													name="CompanyName"
													type="text"
													className="form-control"
													value={form.personalInfo.CompanyName}
													onChange={this.updateForm("personalInfo")}
												/>
											</div>
											<div
												className={classNames("form-group", {
													"has-error": validations.personalInfo.Password,
												})}
											>
												<label
													htmlFor="Password"
													className="control-label"
												>
													{lang.t("Password")}
												</label>
												<input
													id="Password"
													name="Password"
													type="password"
													className="form-control"
													value={form.personalInfo.Password}
													onChange={this.updatePassword}
												/>
											</div>
											<div
												className={classNames("form-group", {
													"has-error": validations.personalInfo.Password,
												})}
											>
												<label
													htmlFor="ConfirmPassword"
													className="control-label"
												>
													{lang.t("Confirm Password")}
												</label>
												<input
													id="ConfirmPassword"
													name="ConfirmPassword"
													type="password"
													className="form-control"
													value={form.personalInfo.ConfirmPassword}
													onChange={this.updateForm("personalInfo")}
												/>
											</div>
											<div
												className={classNames("form-group", {
													"has-error":
														validations.personalInfo._serviceTerms,
												})}
											>
												<div
													id="terms-checkbox-wrapper"
													className="checkbox"
												>
													<label htmlFor="agree-terms-checkbox">
														<input
															id="agree-terms-checkbox"
															type="checkbox"
															onChange={this.updateServiceTerms}
															checked={
																form.personalInfo._serviceTerms
															}
														/>
														{`${lang.t("I agree to the")} `}
														<a
															href={`${STATIC_URLS.rootUrl}/terms-agencies`}
															id="terms-of-service"
															target="_blank"
															rel="noopener noreferrer"
														>
															{lang.t("Terms of Service")}
														</a>
														{` ${lang.t("And").toLowerCase()} `}
														<a
															href={`${STATIC_URLS.rootUrl}/privacy`}
															id="privacy-policy"
															target="_blank"
															rel="noreferrer"
														>
															{lang.t("Privacy Policy")}
														</a>
													</label>
												</div>
											</div>
										</div>
										<PanelControls
											nextOptions={{
												icon: "rkf-arrow-right",
												label: "Continue",
												onClick: this.validatePersonalInfoForm,
											}}
										/>
									</When>
									<When condition={step === 1}>
										<div id="contact-info-form">
											<div className="info-message">
												{lang.t("Enter your contact information")}
											</div>
											<div
												className={classNames("form-group", {
													"has-error":
														validations.contactInfo.StreetAddress,
												})}
											>
												<label
													htmlFor="StreetAddress"
													className="control-label"
												>
													{lang.t("Address")}
												</label>
												<input
													id="StreetAddress"
													name="StreetAddress"
													type="text"
													className="form-control"
													value={form.contactInfo.StreetAddress}
													onChange={this.updateForm("contactInfo")}
												/>
											</div>
											<div
												className={classNames("form-group", {
													"has-error": validations.contactInfo.City,
												})}
											>
												<label
													htmlFor="City"
													className="control-label"
												>
													{lang.t("City")}
												</label>
												<input
													id="City"
													name="City"
													type="text"
													className="form-control"
													value={form.contactInfo.City}
													onChange={this.updateForm("contactInfo")}
												/>
											</div>
											<div className="row">
												<div
													className={classNames(
														"col-sm-6",
														"form-group",
														{
															"has-error":
																validations.contactInfo.StateId,
														},
													)}
												>
													<label
														htmlFor="StateId"
														className="control-label"
													>
														{lang.t("State")}
													</label>
													<StateSelection
														id="StateId"
														name="StateId"
														value={form.contactInfo.StateId}
														onChange={this.updateForm("contactInfo")}
													/>
												</div>
												<div
													className={classNames(
														"col-sm-6",
														"form-group",
														{
															"has-error":
																validations.contactInfo.Zip,
														},
													)}
												>
													<label
														htmlFor="Zip"
														className="control-label"
													>
														{lang.t("Zip")}
													</label>
													<input
														id="Zip"
														name="Zip"
														type="tel"
														className="form-control"
														value={form.contactInfo.Zip}
														onChange={this.updateForm("contactInfo")}
													/>
												</div>
											</div>
										</div>
										<PanelControls
											backOptions={{
												label: "Go Back",
												icon: "rkf-arrow-left",
												onClick: this.decrementStep,
											}}
											nextOptions={{
												icon: "rkf-arrow-right",
												label: "Continue",
												onClick: this.validateContactInfoForm,
											}}
										/>
									</When>
									<When condition={step === 2}>
										<div id="coordinator-details-form">
											<div className="info-message">
												{lang.t("Coordinator/Dept Details")}
											</div>
											<div>
												{lang.t(
													"Are you currently an active law enforcement officer_",
												)}
											</div>
											<div className="row radio-selector">
												<div className="radio">
													<label htmlFor="isLeaOfficer_no">
														<input
															id="isLeaOfficer_no"
															name="_isLeaOfficer"
															type="radio"
															value="no"
															onChange={this.updateForm(
																"coordinatorDetails",
															)}
															checked={
																form.coordinatorDetails
																	._isLeaOfficer === "no"
															}
														/>
														No
													</label>
												</div>
												<div className="radio">
													<label htmlFor="isLeaOfficer_yes">
														<input
															id="isLeaOfficer_yes"
															name="_isLeaOfficer"
															type="radio"
															value="yes"
															onChange={this.updateForm(
																"coordinatorDetails",
															)}
															checked={
																form.coordinatorDetails
																	._isLeaOfficer === "yes"
															}
														/>
														{lang.t("Yes")}
													</label>
												</div>
											</div>
											<If
												condition={
													form.coordinatorDetails._isLeaOfficer === "yes"
												}
											>
												<div
													className={classNames("form-group", {
														"has-error":
															validations.coordinatorDetails
																.JurisdictionId,
													})}
												>
													<label
														htmlFor="JurisdictionId"
														className="control-label"
													>
														{lang.t("Department")}
													</label>
													<AsyncSelect
														id="JurisdictionId"
														name="JurisdictionId"
														placeholder="Search..."
														cacheOptions
														loadOptions={utils.debounce(
															utils.loadJurisdictions,
														)}
														onChange={this.updateSelect(
															"coordinatorDetails",
														)}
														isClearable
													/>
												</div>
												<div
													className={classNames("form-group", {
														"has-error":
															validations.coordinatorDetails
																.OfficerRankId,
													})}
												>
													<label
														htmlFor="OfficerRankId"
														className="control-label"
													>
														{lang.t("Rank")}
													</label>
													<ReactSelect
														id="OfficerRankId"
														name="OfficerRankId"
														value={
															form.coordinatorDetails.OfficerRankId
														}
														options={officerRanks}
														onChange={this.updateSelect(
															"coordinatorDetails",
														)}
													/>
												</div>
												<div className="row">
													<div
														className={classNames(
															"col-sm-6",
															"form-group",
															{
																"has-error":
																	validations.coordinatorDetails
																		.YearsOfService,
															},
														)}
													>
														<label
															htmlFor="YearsOfService"
															className="control-label"
														>
															{lang.t("Years Of Service")}
														</label>
														<input
															id="YearsOfService"
															name="YearsOfService"
															type="tel"
															min="0"
															className="form-control"
															value={
																form.coordinatorDetails
																	.YearsOfService
															}
															onChange={this.updateForm(
																"coordinatorDetails",
															)}
														/>
													</div>
													<div
														className={classNames(
															"col-sm-6",
															"form-group",
															{
																"has-error":
																	validations.coordinatorDetails
																		.YearsWithCurrentDepartment,
															},
														)}
													>
														<label
															htmlFor="YearsWithCurrentDepartment"
															className="control-label"
														>
															{lang.t(
																"Years with current department",
															)}
														</label>
														<input
															id="YearsWithCurrentDepartment"
															name="YearsWithCurrentDepartment"
															type="tel"
															min="0"
															className="form-control"
															value={
																form.coordinatorDetails
																	.YearsWithCurrentDepartment
															}
															onChange={this.updateForm(
																"coordinatorDetails",
															)}
														/>
													</div>
												</div>
											</If>
											<div className="separator" />
											<div>
												{lang.t(
													"Are you permitted to charge fees for assisting with the coordination of off-duty jobs_",
												)}
											</div>
											<div className="row radio-selector form-group">
												<div className="radio">
													<label htmlFor="PermittedToCharge_no">
														<input
															id="PermittedToCharge_no"
															name="PermittedToCharge"
															type="radio"
															value="no"
															onChange={this.updateForm(
																"coordinatorDetails",
															)}
															checked={
																form.coordinatorDetails
																	.PermittedToCharge === "no"
															}
														/>
														No
													</label>
												</div>
												<div className="radio">
													<label htmlFor="PermittedToCharge_yes">
														<input
															id="PermittedToCharge_yes"
															name="PermittedToCharge"
															type="radio"
															value="yes"
															onChange={this.updateForm(
																"coordinatorDetails",
															)}
															checked={
																form.coordinatorDetails
																	.PermittedToCharge === "yes"
															}
														/>
														{lang.t("Yes")}
													</label>
												</div>
											</div>
											<div
												className={classNames("form-group", {
													"has-error":
														validations.coordinatorDetails
															.NumberOfLocations,
												})}
											>
												<label
													htmlFor="NumberOfLocations"
													className="control-label"
												>
													{lang.t(
														"How many locations do you currently coordinate EJs for_",
													)}
												</label>
												<input
													id="NumberOfLocations"
													name="NumberOfLocations"
													min="0"
													type="tel"
													className="form-control"
													value={
														form.coordinatorDetails.NumberOfLocations
													}
													onChange={this.updateForm("coordinatorDetails")}
												/>
											</div>
											<div
												className={classNames("form-group", {
													"has-error":
														validations.coordinatorDetails
															.ShiftsPerWeek,
												})}
											>
												<label
													htmlFor="ShiftsPerWeek"
													className="control-label"
												>
													{lang.t(
														"How many shifts per week do you currently coordinate EJs for_",
													)}
												</label>
												<input
													id="ShiftsPerWeek"
													name="ShiftsPerWeek"
													min="0"
													type="tel"
													className="form-control"
													value={form.coordinatorDetails.ShiftsPerWeek}
													onChange={this.updateForm("coordinatorDetails")}
												/>
											</div>
										</div>
										<PanelControls
											nextOptions={{
												icon: "rkf-arrow-right",
												label: "Continue",
												onClick: this.validateCoordinatorDetailsForm,
											}}
										/>
									</When>
									<When condition={step === 3}>
										<div id="bank-account-forms">
											<strong className="signup-form-text">
												{lang.t("Bank Account Info")}
											</strong>
											<br />
											<br />
											<div
												className={`row form-group ${validations.bankAccountInfo.account_holder_name ? "has-error" : ""}`}
											>
												<div className="col-xs-12">
													<label htmlFor="account_holder_name">
														{lang.t("Account Holder")}
													</label>
													<input
														type="text"
														name="account_holder_name"
														id="account_holder_name"
														value={
															form.bankAccountInfo.account_holder_name
														}
														onChange={this.updateForm(
															"bankAccountInfo",
														)}
														className="form-control"
													/>
												</div>
											</div>
											<div
												className={`row form-group ${validations.bankAccountInfo.routing_number ? "has-error" : ""}`}
											>
												<div className="col-xs-12">
													<label htmlFor="routing_number">
														{lang.t("Routing Number")}
													</label>
													<input
														type="text"
														name="routing_number"
														id="routing_number"
														value={form.bankAccountInfo.routing_number}
														onChange={this.updateForm(
															"bankAccountInfo",
															true,
														)}
														className="form-control"
														maxLength={9}
													/>
												</div>
											</div>
											<div className="row">
												<div
													className={`col-sm-6 col-xs-12 form-group
													${validations.bankAccountInfo.account_number ? " has-error" : ""}`}
												>
													<div>
														<label htmlFor="account_number">
															{lang.t("Account Number")}
														</label>
													</div>
													<input
														type="text"
														name="account_number"
														id="account_number"
														value={form.bankAccountInfo.account_number}
														onChange={this.updateForm(
															"bankAccountInfo",
															true,
														)}
														className="form-control"
													/>
												</div>
												<div
													className={`col-sm-6 col-xs-12 form-group
													${validations.bankAccountInfo.account_number_confirm ? " has-error" : ""}`}
												>
													<div>
														<label htmlFor="account_number_confirm">
															{lang.t("Confirm Account Number")}
														</label>
													</div>
													<input
														type="text"
														name="account_number_confirm"
														id="account_number_confirm"
														value={
															form.bankAccountInfo
																.account_number_confirm
														}
														onChange={this.updateForm(
															"bankAccountInfo",
															true,
														)}
														className="form-control"
													/>
												</div>
											</div>
											<div
												className={`row form-group ${validations.bankAccountInfo.account_holder_type ? "has-error" : ""}`}
											>
												<div className="col-xs-12">
													<label htmlFor="account_holder_type">
														{lang.t("Account Type")}
													</label>
													<select
														type="text"
														name="account_holder_type"
														id="account_holder_type"
														value={
															form.bankAccountInfo.account_holder_type
														}
														onChange={this.updateForm(
															"bankAccountInfo",
														)}
														className="form-control"
													>
														<For
															each="item"
															of={this.bankAccountTypeOptions}
														>
															<option
																key={`bank_account_type_${item.value}`}
																value={item.value}
															>
																{item.label}
															</option>
														</For>
													</select>
												</div>
											</div>
											<Choose>
												<When
													condition={
														form.bankAccountInfo.account_holder_type ===
														BANK_ACCOUNT_TYPES.INDIVIDUAL.value.toString()
													}
												>
													<div className="row">
														<div
															className={`col-xs-12 col-sm-6 form-group
															${validations.bankAccountInfo.id_number ? " has-error" : ""}`}
														>
															<label htmlFor="id_number">
																{lang.t("Social Security Number")}
															</label>
															<MaskedInput
																type="tel"
																name="id_number"
																id="id_number"
																className="form-control"
																value={
																	form.bankAccountInfo.id_number
																}
																mask={MASK_VALUE.ssnMask}
																onChange={this.updateForm(
																	"bankAccountInfo",
																	true,
																)}
															/>
														</div>
														<div
															className={`col-xs-12 col-sm-6 form-group
															${validations.bankAccountInfo.dateOfBirth ? " has-error" : ""}`}
														>
															<label htmlFor="dateOfBirth">
																{lang.t(
																	"Date of Birth (MM/DD/YYYY)",
																)}
															</label>
															<MaskedInput
																type="tel"
																name="dateOfBirth"
																id="dateOfBirth"
																className="form-control"
																value={
																	form.bankAccountInfo.dateOfBirth
																}
																mask={MASK_VALUE.dobMask}
																onChange={this.updateForm(
																	"bankAccountInfo",
																)}
															/>
														</div>
													</div>
												</When>
												<When
													condition={
														form.bankAccountInfo.account_holder_type ===
														BANK_ACCOUNT_TYPES.COMPANY.value.toString()
													}
												>
													<div className="row">
														<div
															className={`col-xs-12 col-sm-6 form-group
															${validations.bankAccountInfo.tax_id ? " has-error" : ""}`}
														>
															<label htmlFor="tax_id">
																{lang.t("ELVN / Tax ID")}
															</label>
															<input
																type="tel"
																name="tax_id"
																id="tax_id"
																className="form-control"
																value={form.bankAccountInfo.tax_id}
																onChange={this.updateForm(
																	"bankAccountInfo",
																	true,
																)}
															/>
														</div>
														<div
															className={`col-xs-12 col-sm-6 form-group
															${validations.bankAccountInfo.name ? " has-error" : ""}`}
														>
															<label htmlFor="company-name">
																{lang.t("Company Name")}
															</label>
															<input
																type="text"
																name="company-name"
																id="company-name"
																className="form-control"
																value={form.bankAccountInfo.name}
																onChange={this.updateForm(
																	"bankAccountInfo",
																)}
															/>
														</div>
													</div>
												</When>
											</Choose>
											<div className="row">
												<div
													className={`col-xs-12 form-group${validations.personalInfo.MobilePhone ? " has-error" : ""}`}
												>
													<label htmlFor="MobilePhone">
														{lang.t("Phone")}
													</label>
													<MaskedInput
														type="tel"
														name="MobilePhone"
														id="MobilePhone"
														className="form-control"
														value={form.personalInfo.MobilePhone}
														mask={MASK_VALUE.phoneMask}
														onChange={this.updateForm("personalInfo")}
													/>
												</div>
											</div>
											<div
												className={`row form-group${validations.bankAccountInfo.line1 ? " has-error" : ""}`}
											>
												<div className="col-xs-12">
													<label htmlFor="line1">
														{lang.t("Billing Street Address")}
													</label>
													<input
														type="text"
														name="line1"
														id="line1"
														className="form-control"
														value={form.bankAccountInfo.line1}
														onChange={this.updateForm(
															"bankAccountInfo",
														)}
													/>
												</div>
											</div>
											<div
												className={`row form-group${validations.bankAccountInfo.city ? " has-error" : ""}`}
											>
												<div className="col-xs-12">
													<label htmlFor="account-city">
														{lang.t("City")}
													</label>
													<input
														type="text"
														name="city"
														id="account-city"
														className="form-control"
														value={form.bankAccountInfo.city}
														onChange={this.updateForm(
															"bankAccountInfo",
														)}
													/>
												</div>
											</div>
											<div className="row">
												<div
													className={`col-xs-12 col-sm-6 form-group
													${validations.bankAccountInfo.state ? " has-error" : ""}`}
												>
													<label htmlFor="account-state">
														{lang.t("State")}
													</label>
													<StateSelection
														ref={this._stateSelection}
														name="state"
														id="account-state"
														value={form.bankAccountInfo.state}
														onChange={this.updateForm(
															"bankAccountInfo",
														)}
													/>
												</div>
												<div
													className={`col-xs-12 col-sm-6 form-group
													${validations.bankAccountInfo.postal_code ? " has-error" : ""}`}
												>
													<label htmlFor="postal_code">
														{lang.t("Zip Code")}
													</label>
													<input
														type="tel"
														name="postal_code"
														id="postal_code"
														className="form-control"
														maxLength="5"
														value={form.bankAccountInfo.postal_code}
														onChange={this.updateForm(
															"bankAccountInfo",
															true,
														)}
													/>
												</div>
											</div>
											<If
												condition={
													form.bankAccountInfo.account_holder_type ===
													BANK_ACCOUNT_TYPES.INDIVIDUAL.value.toString()
												}
											>
												<div className="row form-group">
													<div
														className={`col-xs-12${validations.bankAccountInfo.photoId ? " has-error" : ""}`}
													>
														<label
															className="photo-id-label"
															htmlFor="photoId"
														>
															{lang.t("Photo ID (Evidence Quality)")}
														</label>
														<input
															type="file"
															accept=".jpeg,.jpg,.png,.tif,.tiff"
															name="photoId"
															id="photoId"
															onChange={this.onPhotoChange}
														/>
														<Image
															src={form.bankAccountInfo.photoPreview}
															className="img-responsive photo-id-image"
															fallbackSrc="avatar"
														/>
													</div>
												</div>
												<If
													condition={
														!form.bankAccountInfo.validImageSelected
													}
												>
													<div className="text-center text-danger">
														{lang.t(
															"Please ensure the selected image has one of the following file extensions:",
														)}
														<br />
														{lang.t("jpeg, jpg, png, tif, tiff")}
													</div>
												</If>
											</If>
											<div
												className={`form-group${validations.bankAccountInfo.fullyAuthorized ? " has-error" : ""}`}
											>
												<div className="checkbox">
													<label htmlFor="fullyAuthorized">
														<input
															type="checkbox"
															name="fullyAuthorized"
															id="fullyAuthorized"
															onChange={
																this.updateAccountAuthorization
															}
															checked={
																form.bankAccountInfo
																	.fullyAuthorized === true
															}
														/>
														{lang.t(
															"I attest that I have full authorization to this account",
														)}
													</label>
												</div>
											</div>
										</div>
										<PanelControls
											backOptions={{
												label: "Skip for now",
												onClick: this.incrementStep,
											}}
											nextOptions={{
												icon: "rkf-arrow-right",
												label: "Continue",
												onClick: this.validateBankAccountForms,
											}}
										/>
									</When>
									<When condition={step === 4}>
										<div
											id="finish-setup"
											className="text-center"
										>
											<h1>{lang.t("You are all set!")}</h1>
											<span id="finish-subtitle">
												{lang.t("Your coordinator account is now set up")}
											</span>
											<span id="bottom-text">
												{`${lang.t("Thank you for your submission_")}
												${lang.t("An email has been sent to verify your email address_")}
												${lang.t("You are also currently in our queue to verify your information_")}
												${lang.t("You will receive an email when your account has been verified and activated within the next 2-3 business days_")}
												`}
											</span>
											<a
												href={STATIC_URLS.rootUrl}
												className="btn btn-primary"
											>
												{lang.t("Go to rollkall dot com")}
											</a>
										</div>
									</When>
								</Choose>
								<Loader active={loading} />
							</div>
						</div>
						<If condition={step !== 3}>
							<div
								id="rollkall-promo"
								className="hidden-xs hidden-sm col-md-5 col-lg-4 col-lg-push-1"
							>
								<div>
									<Image
										src={
											step === 2
												? "/images/rollkall_promo_2.png"
												: "/images/rollkall_promo_1.png"
										}
									/>
									<div>
										<h2>{lang.t("RollKall For Coordinator")}</h2>
										<p>
											{lang.t(
												"Great benefits await by using RollKall, including",
											)}
										</p>
										<ol type="1">
											<li>{lang.t("Manage all of your officers")}</li>
											<li>{lang.t("Approve or decline requests")}</li>
											<li>{lang.t("Approve payments easily")}</li>
										</ol>
									</div>
								</div>
							</div>
						</If>
					</div>
				</div>
			</div>
		);
	}
}

export default CoordinatorSignup;
